import { Box, IconButton, InputAdornment, Modal } from '@mui/material';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useState } from 'react';

import usePatch from '@/hooks/usePatch';

import BaseInputValidation from '@/components/ui/Input/BaseInputValidation';
import ButtonCloseModal from '@/components/ui/Button/ButtonCloseModal';
import BaseSelect from '@/components/ui/Select/BaseSelect';

import ModalStyle from '@/constants/ModalStyle';
import { toast } from 'react-toastify';

const schema = yup.object().shape({
	email: yup.string().required().email(),
	username: yup.string().required(),
	phonenumber: yup.string().required(),
	privilegelevelid: yup.string().required(),
});

export const InputPassword = ({ name, label, control, errors }) => {
	const [showPassword, setShowPassword] = useState(false);

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};
	return (
		<BaseInputValidation
			control={control}
			name={name}
			label={label}
			errors={errors}
			config={{ type: showPassword ? 'text' : 'password' }}
			InputProps={{
				endAdornment: (
					<InputAdornment position='end'>
						<IconButton aria-label='toggle password visibility' onClick={handleClickShowPassword} edge='end'>
							{showPassword ? <VisibilityOff /> : <Visibility />}
						</IconButton>
					</InputAdornment>
				),
			}}
		/>
	);
};

export default function DetailUser(props) {
	const { open, onClose, data, onSave, isAllowed } = props;

	const {
		handleSubmit,
		control,
		reset,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			name: data.surename,
			username: data.username,
			email: data.email,
			status: data.status,
			privilegelevelid: data.privilegelevelid,
			phonenumber: data.phonenumber,
		},
	});

	const handleClose = () => {
		reset();
		onClose();
	};

	const { mutate: updateUser } = usePatch({
		url: `usertabledoc/${data.userid}`,
		method: 'patch',
		callback: async (res) => {
			onSave();
			if ([200, 201].includes(res.status) && res.data) {
				toast.success(res.data?.message || `User ${res.status === 200 ? 'updated' : 'created'} successfully`);
			}
		},
		onError: (res) => {
			if (Array.isArray(res.data)) {
				res.data.forEach((err) => {
					if (typeof err === 'string') toast.error(err || `Error`);
				});
			}
		},
	});

	const onSubmit = (data) => {
		if (!isAllowed) return;
		updateUser(data);
		handleClose();
	};

	return (
		<Modal open={open} onClose={handleClose} aria-labelledby='modal-notes' aria-describedby='modal-notes' className='w-full'>
			<Box sx={{ ...ModalStyle, top: '25%' }} className='flex flex-col gap-4 w-[90%] md:w-[768px] mt-4'>
				<span className='text-2xl modal-title'>Endre user</span>
				<ButtonCloseModal onClose={handleClose} />
				<hr />

				<form onSubmit={handleSubmit(onSubmit)} className='flex flex-col w-full gap-4'>
					<div className='grid w-full grid-cols-12 gap-2'>
						<div className='flex flex-col items-start col-span-6 gap-4'>
							<BaseInputValidation control={control} name='name' label='Navn' errors={errors} required />
							<BaseInputValidation
								control={control}
								name='username'
								label='Brukernavn'
								errors={errors}
								required
								config={{
									disableAutofill: true,
								}}
							/>
							<BaseInputValidation control={control} name='email' label='E-post' errors={errors} required />
							<BaseInputValidation control={control} name='phonenumber' label='Telefonnummer' errors={errors} required />
						</div>
						<div className='flex flex-col items-start col-span-6 gap-4'>
							<BaseSelect
								control={control}
								name='status'
								label='Sett aktiv'
								errors
								data={[
									{ id: 1, name: 'Aktiv', value: '1' },
									{ id: 2, name: 'Ikke aktiv', value: '2' },
									{ id: 3, name: 'Arkivert', value: '3' },
								]}
								config={{ styles: {}, classes: '', optionLabel: '--Select Status--' }}
								size='small'
							/>
							<BaseSelect
								control={control}
								name='privilegelevelid'
								label='Kontotype'
								errors
								data={[
									{ privilegelevelname: 'Developer', privilegelevelid: '1' },
									{ privilegelevelname: 'Administrasjon', privilegelevelid: '2' },
									{ privilegelevelname: 'Produktsjef', privilegelevelid: '3' },
									{ privilegelevelname: 'Prosjektleder', privilegelevelid: '4' },
									{ privilegelevelname: 'Ansatt', privilegelevelid: '5' },
									{ privilegelevelname: 'Public', privilegelevelid: '6' },
									{ privilegelevelname: 'Ekstern bruker', privilegelevelid: '7' },
									{ privilegelevelname: 'Read only', privilegelevelid: '8' },
								]}
								config={{
									styles: {},
									classes: '',
									optionLabel: '--Select Status--',
									keyVal: 'privilegelevelid',
									keyName: 'privilegelevelname',
									keyLabel: 'privilegelevelname',
								}}
								size='small'
							/>
							<InputPassword
								name='password'
								label='Passord'
								control={control}
								errors={errors}
								config={{
									disableAutofill: true,
								}}
							/>
							<InputPassword name='newpassword' label='Bekreft passord' control={control} errors={errors} />
						</div>
					</div>

					{isAllowed && (
						<div className='flex gap-4 ml-auto'>
							<button className='px-3 py-2 text-white rounded-md btn bg-primary' type='submit'>
								Oppdater
							</button>
						</div>
					)}
				</form>
			</Box>
		</Modal>
	);
}
